import React, { useState, useRef } from "react";
import {
  FaArrowLeft,
  FaArrowRight,
  FaCheck,
  FaUpload,
  FaBuilding,
  FaUsers,
  FaEye,
  FaImage,
  FaTimes,
  FaUserGraduate,
  FaChalkboardTeacher,
} from "react-icons/fa";
import { organizationService } from "../../services/organizationService";
import {
  databaseService,
  OrganizationData,
} from "../../services/databaseService";
import { dimensionsTemplate } from "../../App";

// Sistema de progreso visual para la creación de organizaciones
interface ProgressStep {
  id: string;
  name: string;
  icon: string;
  total: number;
  current: number;
  status: 'pending' | 'in-progress' | 'completed' | 'error';
  startTime?: number;
}

interface CreationProgress {
  currentStep: string;
  steps: ProgressStep[];
  overallProgress: number;
  estimatedTimeRemaining: string;
  isActive: boolean;
}

// Función para limpiar IDs y otros campos de caracteres extraños
const cleanField = (value: string): string => {
  if (!value) return '';
  return value
    .toString()
    .trim()
    .replace(/^["'\t\s]+|["'\t\s]+$/g, '') // Remover comillas y tabulaciones al inicio/final
    .replace(/\t/g, '') // Remover tabulaciones en el medio
    .replace(/[""'']/g, '"') // Normalizar comillas
    .replace(/^\"|\"$/g, '') // Remover comillas externas
    .trim();
};

interface CompleteOrganizationWizardProps {
  onOrganizationCreated: (organization: OrganizationData) => void;
  onCancel: () => void;
}

interface OrganizationFormData {
  displayName: string;
  tenantKey: string;
  domains: string[];
  status: "active" | "trial";
  branding: {
    logoUrl: string;
    faviconUrl: string;
    colors: {
      primary: string;
      secondary: string;
      accent?: string;
    };
  };
  settings: {
    maxUsers: number;
    maxStudents: number;
    features: string[];
  };
  adminUser: {
    username: string;
    email: string;
    firstName: string;
    lastName: string;
    password: string;
  };
  teachers: TeacherData[];
  students: StudentData[];
  courses: CourseData[];
}

interface TeacherData {
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  role: "admin" | "teacher" | "viewer";
  courses: string[];
  allowedDimensions: number[];
  phone: string;
  password: string;
}

interface StudentData {
  id: string;
  name: string;
  firstName: string;
  lastName: string;
  course: string;
  gender: "M" | "F";
  birthDate: string;
  email: string;
}

interface CourseData {
  name: string;
  level: string;
  teacherEmails: string[];
}

export const CompleteOrganizationWizard: React.FC<
  CompleteOrganizationWizardProps
> = ({ onOrganizationCreated, onCancel }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Estado del sistema de progreso
  const [creationProgress, setCreationProgress] = useState<CreationProgress>({
    currentStep: '',
    steps: [],
    overallProgress: 0,
    estimatedTimeRemaining: '',
    isActive: false
  });

  // Refs para los inputs de archivo
  const teacherFileInputRef = useRef<HTMLInputElement>(null);
  const studentFileInputRef = useRef<HTMLInputElement>(null);

  // Funciones del sistema de progreso
  const initializeProgress = (teachersCount: number, coursesCount: number, studentsCount: number) => {
    const steps: ProgressStep[] = [
      {
        id: 'teachers',
        name: 'Creando Profesores',
        icon: '👨‍🏫',
        total: teachersCount,
        current: 0,
        status: 'pending'
      },
      {
        id: 'courses',
        name: 'Creando Cursos',
        icon: '📚',
        total: coursesCount,
        current: 0,
        status: 'pending'
      },
      {
        id: 'students',
        name: 'Creando Estudiantes',
        icon: '🧑‍🎓',
        total: studentsCount,
        current: 0,
        status: 'pending'
      }
    ];

    setCreationProgress({
      currentStep: 'teachers',
      steps,
      overallProgress: 0,
      estimatedTimeRemaining: 'Calculando...',
      isActive: true
    });
  };

  const updateStepProgress = (stepId: string, current: number, status?: 'pending' | 'in-progress' | 'completed' | 'error') => {
    setCreationProgress(prev => {
      const newSteps = prev.steps.map(step => {
        if (step.id === stepId) {
          const updatedStep = { 
            ...step, 
            current: Math.min(current, step.total),
            status: status || (current === step.total ? 'completed' : current > 0 ? 'in-progress' : 'pending')
          };
          
          // Marcar tiempo de inicio si es la primera vez que empieza
          if (current > 0 && !step.startTime) {
            updatedStep.startTime = Date.now();
          }
          
          return updatedStep;
        }
        return step;
      });

      // Calcular progreso general
      const totalItems = newSteps.reduce((sum, step) => sum + step.total, 0);
      const completedItems = newSteps.reduce((sum, step) => sum + step.current, 0);
      const overallProgress = totalItems > 0 ? Math.round((completedItems / totalItems) * 100) : 0;

      // Calcular tiempo estimado
      const currentStepData = newSteps.find(s => s.id === stepId);
      let estimatedTimeRemaining = 'Calculando...';
      
      if (currentStepData && currentStepData.startTime && currentStepData.current > 0) {
        const elapsedTime = Date.now() - currentStepData.startTime;
        const avgTimePerItem = elapsedTime / currentStepData.current;
        const remainingInStep = currentStepData.total - currentStepData.current;
        const remainingSteps = newSteps.filter((s, index) => 
          newSteps.findIndex(step => step.id === s.id) > newSteps.findIndex(step => step.id === stepId)
        );
        const remainingItems = remainingInStep + remainingSteps.reduce((sum, step) => sum + step.total, 0);
        const estimatedMs = remainingItems * avgTimePerItem;
        
        if (estimatedMs > 0) {
          const minutes = Math.floor(estimatedMs / 60000);
          const seconds = Math.floor((estimatedMs % 60000) / 1000);
          estimatedTimeRemaining = minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
        } else {
          estimatedTimeRemaining = 'Casi listo...';
        }
      }

      // Actualizar paso actual
      let currentStep = prev.currentStep;
      const currentStepIndex = newSteps.findIndex(s => s.id === currentStep);
      if (currentStepIndex !== -1 && newSteps[currentStepIndex].status === 'completed') {
        const nextStep = newSteps.find(s => s.status !== 'completed');
        if (nextStep) {
          currentStep = nextStep.id;
        }
      }

      return {
        ...prev,
        steps: newSteps,
        overallProgress,
        estimatedTimeRemaining,
        currentStep
      };
    });
  };

  const completeProgress = () => {
    setCreationProgress(prev => ({
      ...prev,
      isActive: false,
      overallProgress: 100,
      estimatedTimeRemaining: 'Completado',
      steps: prev.steps.map(step => ({ ...step, status: 'completed' as const }))
    }));
  };

  const [formData, setFormData] = useState<OrganizationFormData>({
    displayName: "",
    tenantKey: "",
    domains: [""],
    status: "trial",
    branding: {
      logoUrl: "",
      faviconUrl: "",
      colors: {
        primary: "#3B82F6",
        secondary: "#10B981",
        accent: "#F59E0B",
      },
    },
    settings: {
      maxUsers: 50,
      maxStudents: 500,
      features: [
        "basic_evaluation",
        "reports",
        "user_management",
        "data_import",
      ],
    },
    adminUser: {
      username: "",
      email: "",
      firstName: "",
      lastName: "",
      password: "",
    },
    teachers: [],
    students: [],
    courses: [],
  });

  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string>("");
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
  const [selectedLevels, setSelectedLevels] = useState<string[]>([]);
  const [lettersPerLevel, setLettersPerLevel] = useState<{
    [key: string]: number;
  }>({});

  const steps = [
    { id: 1, title: "Configuración General", icon: FaBuilding },
    { id: 2, title: "Profesores", icon: FaChalkboardTeacher },
    { id: 3, title: "Estudiantes", icon: FaUserGraduate },
    { id: 4, title: "Cursos y Asignación", icon: FaUsers },
    { id: 5, title: "Revisión", icon: FaEye },
  ];

  const organizationTemplates = {
    "medio-mayor": {
      name: "Desde Medio Mayor",
      description:
        "Plantilla para jardines infantiles y colegios desde Medio Mayor hasta 8° básico",
      courses: [
        { name: "Medio Mayor A", level: "Parvularia" },
        { name: "Pre Kinder A", level: "Parvularia" },
        { name: "Kinder A", level: "Parvularia" },
        { name: "1° Básico A", level: "Básico" },
        { name: "2° Básico A", level: "Básico" },
        { name: "3° Básico A", level: "Básico" },
        { name: "4° Básico A", level: "Básico" },
        { name: "5° Básico A", level: "Básico" },
        { name: "6° Básico A", level: "Básico" },
        { name: "7° Básico A", level: "Básico" },
        { name: "8° Básico A", level: "Básico" },
      ],
      sampleTeachers: [
        {
          firstName: "María",
          lastName: "González",
          role: "admin",
          courses: ["Medio Mayor A"],
        },
        {
          firstName: "Ana",
          lastName: "Martínez",
          role: "teacher",
          courses: ["1° Básico A"],
        },
      ],
    },
    "pre-kinder": {
      name: "Desde Pre Kinder",
      description: "Plantilla para colegios desde Pre Kinder hasta 8° básico",
      courses: [
        { name: "Pre Kinder A", level: "Parvularia" },
        { name: "Kinder A", level: "Parvularia" },
        { name: "1° Básico A", level: "Básico" },
        { name: "2° Básico A", level: "Básico" },
        { name: "3° Básico A", level: "Básico" },
        { name: "4° Básico A", level: "Básico" },
        { name: "5° Básico A", level: "Básico" },
        { name: "6° Básico A", level: "Básico" },
        { name: "7° Básico A", level: "Básico" },
        { name: "8° Básico A", level: "Básico" },
      ],
      sampleTeachers: [
        {
          firstName: "María",
          lastName: "González",
          role: "admin",
          courses: ["Pre Kinder A"],
        },
        {
          firstName: "Carlos",
          lastName: "Rodríguez",
          role: "teacher",
          courses: ["1° Básico A"],
        },
      ],
    },
    "colegio-basico": {
      name: "Colegio Básico",
      description:
        "Plantilla para colegios de educación básica (1° a 8° básico)",
      courses: [
        { name: "1° Básico A", level: "Básico" },
        { name: "2° Básico A", level: "Básico" },
        { name: "3° Básico A", level: "Básico" },
        { name: "4° Básico A", level: "Básico" },
        { name: "5° Básico A", level: "Básico" },
        { name: "6° Básico A", level: "Básico" },
        { name: "7° Básico A", level: "Básico" },
        { name: "8° Básico A", level: "Básico" },
      ],
      sampleTeachers: [
        {
          firstName: "María",
          lastName: "González",
          role: "admin",
          courses: ["1° Básico A"],
        },
        {
          firstName: "Juan",
          lastName: "Pérez",
          role: "teacher",
          courses: ["3° Básico A"],
        },
      ],
    },
    personalizado: {
      name: "Personalizado",
      description: "Crear organización desde cero sin plantilla predefinida",
      courses: [],
      sampleTeachers: [],
    },
  };

  // Todos los niveles educativos disponibles
  const allEducationalLevels = [
    "Medio Mayor",
    "Pre Kinder",
    "Kinder",
    "1° Básico",
    "2° Básico",
    "3° Básico",
    "4° Básico",
    "5° Básico",
    "6° Básico",
    "7° Básico",
    "8° Básico",
    "1° Medio",
    "2° Medio",
    "3° Medio",
    "4° Medio",
  ];

  const generateTenantKey = (displayName: string) => {
    return displayName
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, "")
      .replace(/\s+/g, "")
      .replace(/^-|-$/g, "");
  };

  const generateSecurePassword = () => {
    const chars =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*";
    let password = "";
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return password;
  };

  // Función para generar cursos automáticamente a partir de los profesores importados
  const generateCoursesFromTeachers = (courseNames: string[], consolidatedTeachers: TeacherData[]) => {
    console.log("📚 Generando cursos automáticamente:", courseNames);
    
    const newCourses: CourseData[] = courseNames.map(courseName => {
      // Determinar el nivel basado en el nombre del curso
      let level = "Básico"; // nivel por defecto
      
      if (courseName.toLowerCase().includes("kinder") || 
          courseName.toLowerCase().includes("kínder") ||
          courseName.toLowerCase().includes("medio mayor") ||
          courseName.toLowerCase().includes("prekinder") ||
          courseName.toLowerCase().includes("prekínder")) {
        level = "Parvularia";
      } else if (courseName.toLowerCase().includes("medio") ||
                courseName.toLowerCase().includes("1°") ||
                courseName.toLowerCase().includes("2°") ||
                courseName.toLowerCase().includes("3°") ||
                courseName.toLowerCase().includes("4°")) {
        level = "Medio";
      }

      // Obtener profesores asignados a este curso desde los profesores consolidados
      const assignedTeachers = consolidatedTeachers
        .filter(teacher => teacher.courses.includes(courseName))
        .map(teacher => teacher.email);

      return {
        name: courseName,
        level: level,
        teacherEmails: assignedTeachers
      };
    });

    console.log("📚 Cursos generados:", newCourses);

    // Actualizar formData con los nuevos cursos
    setFormData(prev => ({
      ...prev,
      courses: newCourses
    }));

    console.log("✅ Cursos agregados automáticamente al formulario");
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev) => {
      const newData = { ...prev, [field]: value };

      if (field === "displayName" && value) {
        const tenantKey = generateTenantKey(value);
        const domain = `${tenantKey}.cl`;

        newData.tenantKey = tenantKey;
        newData.domains = [domain];
        newData.adminUser = {
          ...prev.adminUser,
          username: "admin",
          email: `admin@${domain}`,
          firstName: "Administrador",
          lastName: "Sistema",
          password: prev.adminUser.password || "admin123",
        };
      }

      return newData;
    });
  };

  const handleNestedInputChange = (
    section: string,
    field: string,
    value: any
  ) => {
    setFormData((prev) => ({
      ...prev,
      [section]: {
        ...((prev[section as keyof OrganizationFormData] as any) || {}),
        [field]: value,
      },
    }));
  };

  const extractColorsFromImage = (imageUrl: string): Promise<{primary: string, secondary: string, accent: string}> => {
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        if (!ctx) {
          resolve({
            primary: "#3B82F6",
            secondary: "#10B981", 
            accent: "#F59E0B"
          });
          return;
        }

        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);

        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;
        
        // Extraer colores dominantes
        const colorMap = new Map<string, number>();
        
        // Muestrear cada 10 píxeles para mejor rendimiento
        for (let i = 0; i < data.length; i += 40) {
          const r = data[i];
          const g = data[i + 1];
          const b = data[i + 2];
          const a = data[i + 3];
          
          // Ignorar píxeles transparentes o muy claros/oscuros
          if (a < 128 || (r > 240 && g > 240 && b > 240) || (r < 15 && g < 15 && b < 15)) {
            continue;
          }
          
          // Agrupar colores similares
          const roundedR = Math.round(r / 32) * 32;
          const roundedG = Math.round(g / 32) * 32;
          const roundedB = Math.round(b / 32) * 32;
          
          const colorKey = `${roundedR},${roundedG},${roundedB}`;
          colorMap.set(colorKey, (colorMap.get(colorKey) || 0) + 1);
        }
        
        // Obtener los 3 colores más frecuentes
        const sortedColors = Array.from(colorMap.entries())
          .sort((a, b) => b[1] - a[1])
          .slice(0, 3);
        
        const rgbToHex = (r: number, g: number, b: number) => {
          return "#" + [r, g, b].map(x => {
            const hex = x.toString(16);
            return hex.length === 1 ? "0" + hex : hex;
          }).join("");
        };
        
        let primary = "#3B82F6";
        let secondary = "#10B981";
        let accent = "#F59E0B";
        
        if (sortedColors.length > 0) {
          const [r1, g1, b1] = sortedColors[0][0].split(',').map(Number);
          primary = rgbToHex(r1, g1, b1);
        }
        
        if (sortedColors.length > 1) {
          const [r2, g2, b2] = sortedColors[1][0].split(',').map(Number);
          secondary = rgbToHex(r2, g2, b2);
        }
        
        if (sortedColors.length > 2) {
          const [r3, g3, b3] = sortedColors[2][0].split(',').map(Number);
          accent = rgbToHex(r3, g3, b3);
        }
        
        resolve({ primary, secondary, accent });
      };
      
      img.onerror = () => {
        resolve({
          primary: "#3B82F6",
          secondary: "#10B981",
          accent: "#F59E0B"
        });
      };
      
      img.src = imageUrl;
    });
  };

  const handleFileUpload = async (file: File) => {
    const reader = new FileReader();
    reader.onload = async (e) => {
      const dataUrl = e.target?.result as string;
      setLogoFile(file);
      setLogoPreview(dataUrl);
      
      // Extraer colores del logo
      try {
        const extractedColors = await extractColorsFromImage(dataUrl);
        
        // Actualizar los colores en el formulario
        setFormData(prev => ({
          ...prev,
          branding: {
            ...prev.branding,
            colors: extractedColors
          }
        }));
        
        console.log('Colores extraídos del logo:', extractedColors);
      } catch (error) {
        console.error('Error extrayendo colores:', error);
      }
    };
    reader.readAsDataURL(file);
  };

  const applyTemplate = (templateKey: string) => {
    const template =
      organizationTemplates[templateKey as keyof typeof organizationTemplates];
    if (!template) return;

    setSelectedTemplate(templateKey);

    if (templateKey === "personalizado") {
      return;
    }

    const courses: CourseData[] = template.courses.map((course) => ({
      name: course.name,
      level: course.level,
      teacherEmails: [],
    }));

    const teachers: TeacherData[] = template.sampleTeachers.map((teacher) => ({
      username: `${teacher.firstName.toLowerCase()}.${teacher.lastName.toLowerCase()}`,
      email: `${teacher.firstName.toLowerCase()}.${teacher.lastName.toLowerCase()}@${
        formData.domains[0] || "ejemplo.cl"
      }`,
      firstName: teacher.firstName,
      lastName: teacher.lastName,
      role: teacher.role as "admin" | "teacher" | "viewer",
      courses: teacher.courses,
      allowedDimensions: [],
      phone: "",
      password: generateSecurePassword(),
    }));

    setFormData((prev) => ({
      ...prev,
      courses,
      teachers,
    }));
  };

  const generateCourses = () => {
    const courses: CourseData[] = [];

    selectedLevels.forEach((level) => {
      const letterCount = lettersPerLevel[level] || 2; // Por defecto 2 cursos (A y B)

      for (let i = 0; i < letterCount; i++) {
        const letter = String.fromCharCode("A".charCodeAt(0) + i);
        courses.push({
          name: `${level} ${letter}`,
          level: level,
          teacherEmails: [],
        });
      }
    });

    setFormData((prev) => ({ ...prev, courses }));
  };

  // Funciones CRUD
  const addTeacher = () => {
    const newTeacher: TeacherData = {
      username: "",
      email: "",
      firstName: "",
      lastName: "",
      role: "teacher",
      courses: [],
      allowedDimensions: [],
      phone: "",
      password: generateSecurePassword(),
    };
    setFormData((prev) => ({
      ...prev,
      teachers: [...prev.teachers, newTeacher],
    }));
  };

  const updateTeacher = (index: number, field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      teachers: prev.teachers.map((teacher, i) =>
        i === index ? { ...teacher, [field]: value } : teacher
      ),
    }));
  };

  const removeTeacher = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      teachers: prev.teachers.filter((_, i) => i !== index),
    }));
  };

  const addStudent = () => {
    const newStudent: StudentData = {
      id: "",
      name: "",
      firstName: "",
      lastName: "",
      course: "",
      gender: "M",
      birthDate: "",
      email: "",
    };
    setFormData((prev) => ({
      ...prev,
      students: [...prev.students, newStudent],
    }));
  };

  const updateStudent = (index: number, field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      students: prev.students.map((student, i) =>
        i === index ? { ...student, [field]: value } : student
      ),
    }));
  };

  const removeStudent = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      students: prev.students.filter((_, i) => i !== index),
    }));
  };

  const addCourse = () => {
    const newCourse: CourseData = {
      name: "",
      level: "",
      teacherEmails: [],
    };
    setFormData((prev) => ({
      ...prev,
      courses: [...prev.courses, newCourse],
    }));
  };

  const updateCourse = (index: number, field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      courses: prev.courses.map((course, i) =>
        i === index ? { ...course, [field]: value } : course
      ),
    }));
  };

  const removeCourse = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      courses: prev.courses.filter((_, i) => i !== index),
    }));
  };

  // Función para importar estudiantes desde CSV
  const importStudentsFromCSV = (file: File) => {
    try {
      const reader = new FileReader();

      reader.onerror = () => {
        setError("Error al leer el archivo CSV. Por favor intenta de nuevo.");
      };

      reader.onload = (e) => {
        try {
          const csv = e.target?.result as string;

          if (!csv) {
            setError("El archivo está vacío o no se pudo leer.");
            return;
          }

          const lines = csv.split("\n").filter((line) => line.trim());

          if (lines.length < 2) {
            setError(
              "El archivo debe tener al menos una fila de encabezados y una fila de datos"
            );
            return;
          }

          // Detectar el separador (coma o punto y coma)
          const firstLine = lines[0];
          const separator = firstLine.includes(";") ? ";" : ",";
          console.log("Separador detectado:", separator);

          const headers = firstLine.split(separator).map((h) => h.trim());
          const students: StudentData[] = [];

          console.log("Headers detectados:", headers);
          console.log("Total de líneas:", lines.length);

          // Detectar si es el formato específico: Curso, Alumno, RUT, Fecha de Nacimiento, Sexo
          const isSpecificFormat =
            headers.length === 5 &&
            (headers[0].toLowerCase().includes("curso") ||
              headers[1].toLowerCase().includes("alumno") ||
              headers[2].toLowerCase().includes("rut") ||
              headers.some((h) => h.toLowerCase().includes("curso")) ||
              headers.some((h) => h.toLowerCase().includes("alumno")));

          console.log("Formato específico detectado:", isSpecificFormat);
          console.log(
            "Headers analizados:",
            headers.map((h) => h.toLowerCase())
          );

          for (let i = 1; i < lines.length; i++) {
            const values = lines[i].split(separator).map((v) => v.trim());

            console.log(`Línea ${i}:`, values);

            if (values.length < 3) {
              console.log(
                `Saltando línea ${i} - muy pocos valores:`,
                values.length
              );
              continue; // Saltar líneas vacías o incompletas
            }

            let student: StudentData;

            if (isSpecificFormat) {
              // Formato específico: Curso, Alumno, RUT, Fecha de Nacimiento, Sexo
              const fullName = values[1] || "";
              const nameParts = fullName.split(" ");
              const firstName = nameParts[0] || "";
              const lastName = nameParts.slice(1).join(" ") || "";

              // Convertir fecha de DD-MM-YY a YYYY-MM-DD
              let birthDate = values[3] || "";
              if (birthDate && birthDate.match(/^\d{2}-\d{2}-\d{2}$/)) {
                const [day, month, year] = birthDate.split("-");
                const fullYear =
                  parseInt(year) > 50 ? `19${year}` : `20${year}`;
                birthDate = `${fullYear}-${month.padStart(
                  2,
                  "0"
                )}-${day.padStart(2, "0")}`;
              }

              student = {
                id: cleanField(values[2] || ""), // RUT limpio
                name: cleanField(fullName),
                firstName: cleanField(firstName),
                lastName: cleanField(lastName),
                course: cleanField(values[0] || ""), // Curso
                gender: (cleanField(values[4])?.toUpperCase() as "M" | "F") || "M", // Sexo
                birthDate: cleanField(birthDate),
                email: "",
              };
            } else {
              // Formato genérico con headers
              student = {
                id: cleanField(
                  values[headers.indexOf("id")] ||
                  values[headers.indexOf("rut")] ||
                  ""
                ),
                name: cleanField(
                  values[headers.indexOf("name")] ||
                  values[headers.indexOf("alumno")] ||
                  ""
                ),
                firstName: cleanField(values[headers.indexOf("firstName")] || ""),
                lastName: cleanField(values[headers.indexOf("lastName")] || ""),
                course: cleanField(
                  values[headers.indexOf("course")] ||
                  values[headers.indexOf("curso")] ||
                  ""
                ),
                gender: ((
                  cleanField(values[headers.indexOf("gender")] ||
                  values[headers.indexOf("sexo")] || "")
                )?.toUpperCase() === "F"
                  ? "F"
                  : "M") as "M" | "F",
                birthDate: cleanField(
                  values[headers.indexOf("birthDate")] ||
                  values[headers.indexOf("fecha")] ||
                  ""
                ),
                email: cleanField(values[headers.indexOf("email")] || ""),
              };
            }

            console.log("Estudiante creado:", student);
            students.push(student);
          }

          console.log("Total de estudiantes procesados:", students.length);

          if (students.length === 0) {
            setError(
              "No se pudieron procesar estudiantes del archivo CSV. Verifica el formato."
            );
            return;
          }

          setFormData((prev) => ({ ...prev, students }));
          setError(null);
          alert(`Se importaron ${students.length} estudiantes exitosamente.`);
        } catch (parseError) {
          console.error("Error procesando CSV:", parseError);
          setError("Error procesando el archivo CSV. Verifica el formato.");
        }
      };

      // Leer como ArrayBuffer primero para detectar codificación
      const arrayBufferReader = new FileReader();
      arrayBufferReader.onload = (e) => {
        const arrayBuffer = e.target?.result as ArrayBuffer;
        const uint8Array = new Uint8Array(arrayBuffer);
        
        // Intentar decodificar con diferentes codificaciones
        let decodedText = '';
        
        try {
          // Intentar UTF-8 primero
          decodedText = new TextDecoder('utf-8').decode(uint8Array);
          
          // Si contiene caracteres de reemplazo, intentar con Windows-1252
          if (decodedText.includes('�')) {
            console.log('Detectados caracteres problemáticos en estudiantes, intentando Windows-1252');
            decodedText = new TextDecoder('windows-1252').decode(uint8Array);
          }
        } catch (error) {
          console.log('Error con UTF-8 en estudiantes, usando Windows-1252');
          decodedText = new TextDecoder('windows-1252').decode(uint8Array);
        }
        
        console.log('🐛 Contenido estudiantes decodificado', decodedText.substring(0, 100));
        
        // Procesar el texto decodificado
        reader.onload({ target: { result: decodedText } } as any);
      };
      
      arrayBufferReader.readAsArrayBuffer(file);
    } catch (error) {
      console.error("Error importando CSV:", error);
      setError("Error al importar el archivo CSV. Por favor intenta de nuevo.");
    }
  };

  // Función para importar profesores desde CSV
  const importTeachersFromCSV = (file: File) => {
    console.log("🎯 INICIANDO IMPORTACIÓN DE PROFESORES");
    console.log("📁 Archivo seleccionado:", file.name, file.size, "bytes");

    try {
      const reader = new FileReader();

      reader.onerror = () => {
        setError(
          "Error al leer el archivo CSV de profesores. Por favor intenta de nuevo."
        );
      };

      reader.onload = (e) => {
        try {
          const csv = e.target?.result as string;

          if (!csv) {
            setError("El archivo está vacío o no se pudo leer.");
            return;
          }

          const lines = csv.split("\n").filter((line) => line.trim());

          if (lines.length < 2) {
            setError(
              "El archivo debe tener al menos una fila de encabezados y una fila de datos"
            );
            return;
          }

          console.log(
            "📖 ARCHIVO DE PROFESORES LEÍDO - Iniciando procesamiento"
          );
          console.log(
            "📄 Contenido CSV profesores (primeros 200 chars):",
            csv.substring(0, 200)
          );

          // Detectar el separador (coma, punto y coma o tabulación)
          const firstLine = lines[0];
          let separator = ",";
          if (firstLine.includes("\t")) {
            separator = "\t";
          } else if (firstLine.includes(";")) {
            separator = ";";
          }

          console.log("Separador detectado para profesores:", separator);

          const headers = firstLine.split(separator).map((h) => h.trim());
          const teachers: TeacherData[] = [];
          const teacherCourseMap = new Map<string, string[]>(); // Para agrupar cursos por profesor

          console.log("Headers de profesores detectados:", headers);
          console.log("Total de líneas de profesores:", lines.length);

          // Detectar si es el formato específico: Curso, Docente, RUT, Fecha de Nacimiento, Sexo, Correo
          const isSpecificFormat =
            headers.length >= 2 &&
            (headers[0].toLowerCase().includes("curso") ||
              headers[1].toLowerCase().includes("docente") ||
              headers.some((h) => h.toLowerCase().includes("curso")) ||
              headers.some((h) => h.toLowerCase().includes("docente")));

          // Detectar si es el formato de usuarios: NOMBRE;MAIL;CONTRASEÑA;CURSOS
          const isUserPasswordFormat =
            headers.length >= 4 &&
            headers[0].toLowerCase().includes("nombre") &&
            headers[1].toLowerCase().includes("mail") &&
            (headers[2].toLowerCase().includes("contraseña") || headers[2].toLowerCase().includes("password")) &&
            headers[3].toLowerCase().includes("cursos");

          console.log(
            "Formato específico de profesores detectado:",
            isSpecificFormat
          );
          console.log(
            "Formato usuarios y contraseñas detectado:",
            isUserPasswordFormat
          );

          for (let i = 1; i < lines.length; i++) {
            const values = lines[i].split(separator).map((v) => v.trim());

            console.log(`Línea profesor ${i}:`, values);

            if (values.length < 2) {
              console.log(
                `Saltando línea ${i} - muy pocos valores:`,
                values.length
              );
              continue;
            }

            // Validar que tenga al menos Curso y Docente
            const curso = values[0] || "";
            const docente = values[1] || "";

            if (!curso || !docente) {
              console.log(`Saltando línea ${i} - falta curso o docente`);
              continue;
            }

            let teacher: TeacherData;

            if (isUserPasswordFormat) {
              // Formato usuarios: NOMBRE;MAIL;CONTRASEÑA;CURSOS
              const fullName = values[0] || "";
              const email = values[1] || "";
              const password = values[2] || "";
              const cursosText = values[3] || "";

              const nameParts = fullName.split(" ");
              const firstName = nameParts[0] || "";
              const lastName = nameParts.slice(1).join(" ") || "";

              // Generar username desde el email
              const username = email.split("@")[0] || fullName.toLowerCase().replace(/\s+/g, ".");

              // Detectar si es administrador (cursos = "Todos")
              const isAdmin = cursosText.toLowerCase().includes("todos");
              
              // Extraer cursos específicos
              let courses: string[] = [];
              if (!isAdmin) {
                courses = [cursosText.trim()];
              }

              console.log(
                `👤 Procesando usuario: ${fullName} - Email: ${email} - Cursos: ${cursosText} - Es Admin: ${isAdmin}`
              );

              teacher = {
                username: username,
                email: email,
                firstName: firstName,
                lastName: lastName,
                role: isAdmin ? "admin" : "teacher",
                courses: courses,
                allowedDimensions: [], // Todas las dimensiones por defecto
                phone: "",
                password: password, // Usar la contraseña del CSV
              };

              // Agrupar cursos por profesor (mismo email) - Solo para profesores, no administradores
              if (!isAdmin && cursosText.trim()) {
                if (teacherCourseMap.has(email)) {
                  const existingCourses = teacherCourseMap.get(email) || [];
                  if (!existingCourses.includes(cursosText.trim())) {
                    existingCourses.push(cursosText.trim());
                    teacherCourseMap.set(email, existingCourses);
                    console.log(
                      `📚 Agregando curso "${cursosText.trim()}" a profesor ${email}. Cursos actuales:`,
                      existingCourses
                    );
                  }
                } else {
                  teacherCourseMap.set(email, [cursosText.trim()]);
                  console.log(
                    `📚 Primer curso "${cursosText.trim()}" para profesor ${email}`
                  );
                }
              } else if (isAdmin) {
                // Para administradores, no agregar cursos específicos aquí
                if (!teacherCourseMap.has(email)) {
                  teacherCourseMap.set(email, []);
                }
                console.log(
                  `👑 Administrador ${email} - no se asignan cursos específicos`
                );
              }
            } else if (isSpecificFormat) {
              // Formato específico: Curso, Docente, RUT, Fecha de Nacimiento, Sexo, Correo
              const fullName = docente;
              const nameParts = fullName.split(" ");
              const firstName = nameParts[0] || "";
              const lastName = nameParts.slice(1).join(" ") || "";

              // Generar username desde el nombre
              const username = `${firstName.toLowerCase()}.${
                lastName.split(" ")[0]?.toLowerCase() || ""
              }`.replace(/[^a-z.]/g, "");

              const email =
                values[5] ||
                `${username}@${formData.domains[0] || "organizacion.cl"}`;

              // Detectar si es administrador
              const isAdmin =
                curso.toLowerCase().includes("administrador") ||
                curso.toLowerCase().includes("admin") ||
                curso.toLowerCase().includes("director") ||
                curso.toLowerCase().includes("coordinador");

              console.log(
                `👤 Procesando: ${fullName} - Curso: ${curso} - Es Admin: ${isAdmin}`
              );

              teacher = {
                username: cleanField(username),
                email: cleanField(email),
                firstName: cleanField(firstName),
                lastName: cleanField(lastName),
                role: isAdmin ? "admin" : "teacher",
                courses: isAdmin ? [] : [cleanField(curso)], // Administradores no tienen cursos específicos
                allowedDimensions: [], // Todas las dimensiones por defecto
                phone: "",
                password: generateSecurePassword(),
              };

              // Agrupar cursos por profesor (mismo email) - Solo para profesores, no administradores
              if (!isAdmin) {
                if (teacherCourseMap.has(email)) {
                  const existingCourses = teacherCourseMap.get(email) || [];
                  if (!existingCourses.includes(curso)) {
                    existingCourses.push(curso);
                    teacherCourseMap.set(email, existingCourses);
                    console.log(
                      `📚 Agregando curso "${curso}" a profesor ${email}. Cursos actuales:`,
                      existingCourses
                    );
                  }
                } else {
                  teacherCourseMap.set(email, [curso]);
                  console.log(
                    `📚 Primer curso "${curso}" para profesor ${email}`
                  );
                }
              } else {
                // Para administradores, no agregar cursos específicos aquí
                if (!teacherCourseMap.has(email)) {
                  teacherCourseMap.set(email, []);
                }
                console.log(
                  `👑 Administrador ${email} - no se asignan cursos específicos`
                );
              }
            } else {
              // Formato genérico con headers
              teacher = {
                username: cleanField(
                  values[headers.indexOf("username")] ||
                  values[headers.indexOf("usuario")] ||
                  `profesor${i}`
                ),
                email: cleanField(
                  values[headers.indexOf("email")] ||
                  values[headers.indexOf("correo")] ||
                  `profesor${i}@${formData.domains[0] || "organizacion.cl"}`
                ),
                firstName: cleanField(
                  values[headers.indexOf("firstName")] ||
                  values[headers.indexOf("nombre")] ||
                  values[headers.indexOf("docente")] ||
                  ""
                ),
                lastName: cleanField(
                  values[headers.indexOf("lastName")] ||
                  values[headers.indexOf("apellido")] ||
                  ""
                ),
                role: "teacher",
                courses: values[headers.indexOf("courses")]
                  ? values[headers.indexOf("courses")].split(";").map(cleanField)
                  : [cleanField(values[headers.indexOf("curso")] || "")],
                allowedDimensions: [],
                phone: cleanField(
                  values[headers.indexOf("phone")] ||
                  values[headers.indexOf("telefono")] ||
                  ""
                ),
                password: generateSecurePassword(),
              };
            }

            console.log("Profesor creado:", teacher);
            teachers.push(teacher);
          }

          // Si es formato específico, consolidar profesores por email
          if (isSpecificFormat) {
            const consolidatedTeachers: TeacherData[] = [];
            const processedEmails = new Set<string>();

            // Obtener todos los cursos únicos: los de teacherCourseMap + todos los cursos definidos
            const teacherCourses = Array.from(teacherCourseMap.values()).flat();
            const allDefinedCourses = formData.courses.map(c => c.name);
            const allUniqueCourses = Array.from(
              new Set([...teacherCourses, ...allDefinedCourses])
            );

            console.log("📚 Cursos de profesores:", teacherCourses);
            console.log("📚 Cursos definidos:", allDefinedCourses);
            console.log("📚 Todos los cursos únicos:", allUniqueCourses);

            for (const teacher of teachers) {
              if (!processedEmails.has(teacher.email)) {
                if (teacher.role === "admin") {
                  // Administradores tienen TODOS los cursos y dimensiones
                  teacher.courses = allUniqueCourses;
                  teacher.allowedDimensions = []; // Array vacío = todas las dimensiones
                  console.log(
                    `👑 Admin ${teacher.firstName} asignado a TODOS los cursos:`,
                    allUniqueCourses
                  );
                } else {
                  // Profesores tienen solo sus cursos específicos
                  const teacherCourses =
                    teacherCourseMap.get(teacher.email) || [];
                  teacher.courses = teacherCourses;
                  console.log(
                    `👨‍🏫 Profesor ${teacher.firstName} asignado a cursos:`,
                    teacherCourses
                  );
                }

                consolidatedTeachers.push(teacher);
                processedEmails.add(teacher.email);
              }
            }

            // Contar administradores y profesores
            const adminCount = consolidatedTeachers.filter(
              (t) => t.role === "admin"
            ).length;
            const teacherCount = consolidatedTeachers.filter(
              (t) => t.role === "teacher"
            ).length;

            console.log(
              "Profesores consolidados:",
              consolidatedTeachers.length
            );
            console.log(`👑 Administradores: ${adminCount}`);
            console.log(`👨‍🏫 Profesores: ${teacherCount}`);

            setFormData((prev) => ({
              ...prev,
              teachers: consolidatedTeachers,
            }));

            // Generar cursos automáticamente basado en los datos importados
            generateCoursesFromTeachers(allUniqueCourses, consolidatedTeachers);
          } else {
            // Procesar formato de usuarios con contraseñas: NOMBRE;MAIL;CONTRASEÑA;CURSOS
            // Obtener todos los cursos únicos para este formato también
            const teacherCoursesFromMap = Array.from(teacherCourseMap.values()).flat();
            const allDefinedCoursesHere = formData.courses.map(c => c.name);
            const allUniqueCoursesHere = Array.from(
              new Set([...teacherCoursesFromMap, ...allDefinedCoursesHere])
            );
            
            const processedTeachers = teachers.map(teacher => {
              if (teacher.role === "admin") {
                // Administradores tienen TODOS los cursos
                teacher.courses = allUniqueCoursesHere;
                teacher.allowedDimensions = []; // Array vacío = todas las dimensiones
                console.log(
                  `👑 Admin ${teacher.firstName} (formato usuarios) asignado a TODOS los cursos:`,
                  teacher.courses
                );
              }
              return teacher;
            });
            
            setFormData((prev) => ({ ...prev, teachers: processedTeachers }));
          }

          console.log("Total de profesores procesados:", teachers.length);
          setError(null);

          if (isSpecificFormat) {
            const consolidatedTeachers = teachers.filter(
              (t, index, arr) =>
                arr.findIndex((teacher) => teacher.email === t.email) === index
            );
            const adminCount = consolidatedTeachers.filter(
              (t) => t.role === "admin"
            ).length;
            const teacherCount = consolidatedTeachers.filter(
              (t) => t.role === "teacher"
            ).length;

            alert(
              `✅ Importación exitosa!\n\n` +
                `👑 Administradores: ${adminCount}\n` +
                `👨‍🏫 Profesores: ${teacherCount}\n` +
                `📊 Total: ${consolidatedTeachers.length} usuarios`
            );
          } else {
            alert(`Se importaron ${teachers.length} profesores exitosamente.`);
          }
        } catch (parseError) {
          console.error("Error procesando CSV de profesores:", parseError);
          setError(
            "Error procesando el archivo CSV de profesores. Verifica el formato."
          );
        }
      };

      // Leer como ArrayBuffer primero para detectar codificación
      const arrayBufferReader = new FileReader();
      arrayBufferReader.onload = (e) => {
        const arrayBuffer = e.target?.result as ArrayBuffer;
        const uint8Array = new Uint8Array(arrayBuffer);
        
        // Intentar decodificar con diferentes codificaciones
        let decodedText = '';
        
        try {
          // Intentar UTF-8 primero
          decodedText = new TextDecoder('utf-8').decode(uint8Array);
          
          // Si contiene caracteres de reemplazo, intentar con Windows-1252
          if (decodedText.includes('�')) {
            console.log('Detectados caracteres problemáticos, intentando Windows-1252');
            decodedText = new TextDecoder('windows-1252').decode(uint8Array);
          }
        } catch (error) {
          console.log('Error con UTF-8, usando Windows-1252');
          decodedText = new TextDecoder('windows-1252').decode(uint8Array);
        }
        
        console.log('📄 Contenido decodificado (primeros 200 chars):', decodedText.substring(0, 200));
        
        // Procesar el texto decodificado
        reader.onload({ target: { result: decodedText } } as any);
      };
      
      arrayBufferReader.readAsArrayBuffer(file);
    } catch (error) {
      console.error("Error importando CSV de profesores:", error);
      setError(
        "Error al importar el archivo CSV de profesores. Por favor intenta de nuevo."
      );
    }
  };

  const createOrganizationEarly = async () => {
    try {
      setLoading(true);
      setError(null);

      const organizationData = {
        displayName: formData.displayName,
        tenantKey: formData.tenantKey,
        domains: formData.domains.filter((d) => d.trim()),
        status: formData.status,
        branding: formData.branding,
        settings: formData.settings,
        adminUser: formData.adminUser,
      };

      const result = await organizationService.createOrganization(
        organizationData
      );

      if (!result.success) {
        throw new Error(result.message || "Error creando organización");
      }

      console.log("✅ Organización creada exitosamente:", result.organization);
    } catch (error) {
      console.error("Error creando organización:", error);
      setError(
        error instanceof Error ? error.message : "Error creando organización"
      );
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);

      // La organización ya fue creada en el paso 1, obtenerla desde MongoDB
      const orgResult = await organizationService.getOrganization(
        formData.tenantKey
      );

      if (!orgResult.success || !orgResult.organization) {
        throw new Error(
          "No se pudo encontrar la organización. Intenta crear una nueva."
        );
      }

      const organization = orgResult.organization;

      // Actualizar branding si hay logo
      if (logoFile) {
        const logoResult = await organizationService.uploadFile(
          logoFile,
          organization._id,
          "logo"
        );
        if (logoResult.success && logoResult.url) {
          await organizationService.updateOrganization(organization.tenantKey, {
            branding: {
              ...formData.branding,
              logoUrl: logoResult.url,
            },
          });
        }
      }

      // Inicializar el sistema de progreso
      initializeProgress(formData.teachers.length, formData.courses.length, formData.students.length);

      // Crear profesores
      let teachersCreated = 0;
      updateStepProgress('teachers', 0, 'in-progress');
      
      for (const teacherData of formData.teachers) {
        try {
          const result = await organizationService.createTeacher(
            organization._id,
            {
              username: teacherData.username,
              email: teacherData.email,
              firstName: teacherData.firstName,
              lastName: teacherData.lastName,
              role: teacherData.role,
              courses: teacherData.courses,
              phone: teacherData.phone,
              isActive: true,
              mustChangePassword: true,
              password: teacherData.password,
            }
          );
          if (result.success) {
            teachersCreated++;
          }
        } catch (err) {
          console.warn("❌ Error creando profesor:", teacherData.email, err);
        }
        
        // Actualizar progreso en tiempo real
        updateStepProgress('teachers', teachersCreated);
        
        // Pequeña pausa para mostrar el progreso visualmente
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      // Crear cursos
      let coursesCreated = 0;
      updateStepProgress('courses', 0, 'in-progress');
      
      for (const courseData of formData.courses) {
        try {
          const result = await organizationService.createCourse(
            organization._id,
            {
              name: courseData.name,
              level: courseData.level,
              description: '',
              academicYear: new Date().getFullYear(),
              isActive: true,
              teachers: [], // Array vacío inicialmente
              students: [], // Array vacío inicialmente
              settings: {
                maxStudents: 45,
                allowedDimensions: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
                evaluationPeriods: []
              },
              metadata: {
                classroom: '',
                schedule: '',
                notes: ''
              },
              createdBy: 'sistema'
            }
          );
          if (result.success) {
            coursesCreated++;
          }
        } catch (err) {
          console.warn("❌ Error creando curso:", courseData.name, err);
        }
        
        // Actualizar progreso en tiempo real
        updateStepProgress('courses', coursesCreated);
        
        // Pequeña pausa para mostrar el progreso visualmente
        await new Promise(resolve => setTimeout(resolve, 150));
      }

      // Crear estudiantes
      let studentsCreated = 0;
      updateStepProgress('students', 0, 'in-progress');

      for (const studentData of formData.students) {
        try {
          const result = await organizationService.createStudent(
            organization._id,
            {
              id: studentData.id,
              name: studentData.name,
              firstName: studentData.firstName,
              lastName: studentData.lastName,
              course: studentData.course,
              gender: studentData.gender,
              birthDate: studentData.birthDate,
              email: studentData.email,
            }
          );

          if (result.success) {
            studentsCreated++;
          }
        } catch (err) {
          console.error("❌ Error creando estudiante:", studentData.name, err);
        }
        
        // Actualizar progreso en tiempo real
        updateStepProgress('students', studentsCreated);
        
        // Pausa más pequeña para estudiantes (son más)
        await new Promise(resolve => setTimeout(resolve, 50));
      }

      // Completar progreso
      completeProgress();

      // Mostrar mensaje de éxito detallado
      const successMessage = `
🎉 ¡Organización "${organization.displayName}" creada exitosamente!

📊 Resumen de la creación:
• ${teachersCreated} profesor(es) creado(s)
• ${coursesCreated} curso(s) creado(s)  
• ${studentsCreated} estudiante(s) creado(s)

🔑 Para acceder a esta organización:
1. Cierra sesión del panel administrativo
2. Inicia sesión con las credenciales del administrador:
   • Usuario: ${formData.adminUser.username}
   • Email: ${formData.adminUser.email}
   • Contraseña: (la que configuraste)

3. Una vez dentro, podrás:
   • Ver todos los estudiantes importados
   • Gestionar profesores y cursos
   • Realizar evaluaciones
   • Generar reportes

¡La organización está lista para usar!
      `.trim();

      alert(successMessage);

      onOrganizationCreated(organization);
    } catch (error) {
      console.error("Error completing organization setup:", error);
      setError(
        error instanceof Error
          ? error.message
          : "Error completando la configuración"
      );
    } finally {
      setLoading(false);
    }
  };

  // Renderizado de pasos
  const renderStep1 = () => (
    <div className="space-y-8">
      {/* Información Básica */}
      <div className="bg-gray-50 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Información Básica
        </h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nombre de la Organización *
            </label>
            <input
              type="text"
              value={formData.displayName}
              onChange={(e) => handleInputChange("displayName", e.target.value)}
              placeholder="Ej: Colegio San José"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Clave del Tenant *
              </label>
              <input
                type="text"
                value={formData.tenantKey}
                onChange={(e) => handleInputChange("tenantKey", e.target.value)}
                placeholder="colegio-san-jose"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Dominio de Email *
              </label>
              <input
                type="text"
                value={formData.domains[0] || ""}
                onChange={(e) => {
                  const newDomains = [e.target.value];
                  setFormData((prev) => ({ ...prev, domains: newDomains }));
                }}
                placeholder="ejemplo.cl"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Usuario Administrador *
              </label>
              <input
                type="text"
                value={formData.adminUser.username}
                onChange={(e) =>
                  handleNestedInputChange(
                    "adminUser",
                    "username",
                    e.target.value
                  )
                }
                placeholder="admin"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Administrador *
              </label>
              <input
                type="email"
                value={formData.adminUser.email}
                onChange={(e) =>
                  handleNestedInputChange("adminUser", "email", e.target.value)
                }
                placeholder={`admin@${formData.domains[0] || "ejemplo.cl"}`}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nombre *
              </label>
              <input
                type="text"
                value={formData.adminUser.firstName}
                onChange={(e) =>
                  handleNestedInputChange(
                    "adminUser",
                    "firstName",
                    e.target.value
                  )
                }
                placeholder="Administrador"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Apellido *
              </label>
              <input
                type="text"
                value={formData.adminUser.lastName}
                onChange={(e) =>
                  handleNestedInputChange(
                    "adminUser",
                    "lastName",
                    e.target.value
                  )
                }
                placeholder="Sistema"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Contraseña del Administrador *
            </label>
            <div className="relative">
              <input
                type="text"
                value={formData.adminUser.password}
                onChange={(e) =>
                  handleNestedInputChange("adminUser", "password", e.target.value)
                }
                placeholder="admin123"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                <span className="text-xs text-gray-500">Por defecto: admin123</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Selección de Niveles Educativos */}
      <div className="bg-gray-50 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Selecciona los Niveles Educativos
        </h3>
        <p className="text-sm text-gray-600 mb-4">
          Selecciona los niveles que tendrá tu organización y la cantidad de
          cursos por nivel
        </p>

        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {allEducationalLevels.map((level) => (
              <div
                key={level}
                className="border border-gray-200 rounded-lg p-3"
              >
                <div className="flex items-center justify-between mb-2">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={selectedLevels.includes(level)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedLevels((prev) => [...prev, level]);
                          setLettersPerLevel((prev) => ({
                            ...prev,
                            [level]: 2,
                          })); // Por defecto 2 cursos
                        } else {
                          setSelectedLevels((prev) =>
                            prev.filter((l) => l !== level)
                          );
                          setLettersPerLevel((prev) => {
                            const newLetters = { ...prev };
                            delete newLetters[level];
                            return newLetters;
                          });
                        }
                      }}
                      className="mr-2 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="text-sm font-medium text-gray-900">
                      {level}
                    </span>
                  </label>
                </div>

                {selectedLevels.includes(level) && (
                  <div className="mt-2">
                    <label className="block text-xs text-gray-600 mb-1">
                      Cantidad de cursos:
                    </label>
                    <select
                      value={lettersPerLevel[level] || 2}
                      onChange={(e) =>
                        setLettersPerLevel((prev) => ({
                          ...prev,
                          [level]: parseInt(e.target.value),
                        }))
                      }
                      className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                    >
                      {[1, 2, 3, 4, 5, 6].map((num) => (
                        <option key={num} value={num}>
                          {num} curso{num > 1 ? "s" : ""} (A
                          {num > 1
                            ? ` - ${String.fromCharCode(
                                "A".charCodeAt(0) + num - 1
                              )}`
                            : ""}
                          )
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            ))}
          </div>

          {selectedLevels.length > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-medium text-blue-900 mb-2">
                Resumen de Selección:
              </h4>
              <div className="text-sm text-blue-800">
                <p>• {selectedLevels.length} niveles seleccionados</p>
                <p>
                  •{" "}
                  {Object.values(lettersPerLevel).reduce(
                    (sum, count) => sum + count,
                    0
                  )}{" "}
                  cursos totales
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Branding */}
      <div className="bg-gray-50 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Branding</h3>
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Color Primario *
              </label>
              <div className="flex items-center space-x-2">
                <input
                  type="color"
                  value={formData.branding.colors.primary}
                  onChange={(e) =>
                    handleNestedInputChange("branding", "colors", {
                      ...formData.branding.colors,
                      primary: e.target.value,
                    })
                  }
                  className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                />
                <input
                  type="text"
                  value={formData.branding.colors.primary}
                  onChange={(e) =>
                    handleNestedInputChange("branding", "colors", {
                      ...formData.branding.colors,
                      primary: e.target.value,
                    })
                  }
                  placeholder="#1e40af"
                  className="flex-1 border border-gray-300 rounded px-3 py-2"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Color Secundario
              </label>
              <div className="flex items-center space-x-2">
                <input
                  type="color"
                  value={formData.branding.colors.secondary}
                  onChange={(e) =>
                    handleNestedInputChange("branding", "colors", {
                      ...formData.branding.colors,
                      secondary: e.target.value,
                    })
                  }
                  className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                />
                <input
                  type="text"
                  value={formData.branding.colors.secondary}
                  onChange={(e) =>
                    handleNestedInputChange("branding", "colors", {
                      ...formData.branding.colors,
                      secondary: e.target.value,
                    })
                  }
                  placeholder="#64748b"
                  className="flex-1 border border-gray-300 rounded px-3 py-2"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Color de Acento
              </label>
              <div className="flex items-center space-x-2">
                <input
                  type="color"
                  value={formData.branding.colors.accent || "#f59e0b"}
                  onChange={(e) =>
                    handleNestedInputChange("branding", "colors", {
                      ...formData.branding.colors,
                      accent: e.target.value,
                    })
                  }
                  className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                />
                <input
                  type="text"
                  value={formData.branding.colors.accent || "#f59e0b"}
                  onChange={(e) =>
                    handleNestedInputChange("branding", "colors", {
                      ...formData.branding.colors,
                      accent: e.target.value,
                    })
                  }
                  placeholder="#f59e0b"
                  className="flex-1 border border-gray-300 rounded px-3 py-2"
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Logo de la Organización
              </label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                {logoPreview ? (
                  <div className="space-y-4">
                    <img
                      src={logoPreview}
                      alt="Logo preview"
                      className="mx-auto h-20 object-contain"
                    />
                    <button
                      onClick={() => {
                        setLogoFile(null);
                        setLogoPreview("");
                      }}
                      className="text-red-600 hover:text-red-800 text-sm"
                    >
                      <FaTimes className="inline mr-1" />
                      Eliminar logo
                    </button>
                  </div>
                ) : (
                  <div>
                    <FaImage className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                    <p className="text-sm text-gray-600 mb-2">
                      Arrastra una imagen aquí o
                    </p>
                    <label className="cursor-pointer bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 inline-block">
                      <FaUpload className="inline mr-2" />
                      Seleccionar archivo
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            handleFileUpload(file);
                          }
                        }}
                        className="hidden"
                      />
                    </label>
                  </div>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Vista Previa
              </label>
              <div
                className="border rounded-lg p-4 h-32"
                style={{
                  backgroundColor: formData.branding.colors.primary,
                  color: "white",
                }}
              >
                <div className="flex items-center space-x-3">
                  {logoPreview && (
                    <img
                      src={logoPreview}
                      alt="Logo"
                      className="h-8 w-8 object-contain"
                    />
                  )}
                  <div>
                    <h3 className="font-semibold">
                      {formData.displayName || "Nombre de la Organización"}
                    </h3>
                    <p className="text-sm opacity-90">
                      Panel de Evaluación Integral
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h4 className="text-lg font-medium text-gray-900">
          Profesores ({formData.teachers.length})
        </h4>
        <div className="flex space-x-2">
          <button
            onClick={addTeacher}
            className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-sm"
          >
            Agregar Profesor
          </button>
          <button
            onClick={() => {

              teacherFileInputRef.current?.click();
            }}
            className="bg-green-500 hover:bg-green-600 text-white px-3 py-1 rounded text-sm"
          >
            <FaUpload className="inline mr-1" />
            Importar CSV
          </button>
          <input
            ref={teacherFileInputRef}
            type="file"
            accept=".csv"
            className="hidden"
            onChange={(e) => {
              try {
                console.log("� EVENTOi ONCHANGE DISPARADO - Profesores");
                console.log("📋 Event target:", e.target);
                const file = e.target.files?.[0];
                if (file) {
                  console.log("ℹ️ Importando profesores desde CSV", `${file?.name} (${file?.size} bytes)`);
                  importTeachersFromCSV(file);
                  // Reset input para permitir seleccionar el mismo archivo de nuevo
                  e.target.value = "";
                } else {
                  console.log("❌ No se detectó archivo");
                }
              } catch (error) {
                console.error("❌ Error en onChange:", error);
              }
            }}
          />
        </div>
      </div>

      <div className="space-y-4 max-h-96 overflow-y-auto">
        {formData.teachers.map((teacher, index) => (
          <div key={index} className="border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h5 className="font-medium text-gray-900">
                Profesor {index + 1}
              </h5>
              <button
                onClick={() => removeTeacher(index)}
                className="text-red-500 hover:text-red-700"
              >
                <FaTimes />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <input
                type="text"
                placeholder="Nombre de usuario"
                value={teacher.username}
                onChange={(e) =>
                  updateTeacher(index, "username", e.target.value)
                }
                className="border border-gray-300 rounded px-2 py-1 text-sm"
              />
              <input
                type="email"
                placeholder="Email"
                value={teacher.email}
                onChange={(e) => updateTeacher(index, "email", e.target.value)}
                className="border border-gray-300 rounded px-2 py-1 text-sm"
              />
              <input
                type="text"
                placeholder="Nombre"
                value={teacher.firstName}
                onChange={(e) =>
                  updateTeacher(index, "firstName", e.target.value)
                }
                className="border border-gray-300 rounded px-2 py-1 text-sm"
              />
              <input
                type="text"
                placeholder="Apellido"
                value={teacher.lastName}
                onChange={(e) =>
                  updateTeacher(index, "lastName", e.target.value)
                }
                className="border border-gray-300 rounded px-2 py-1 text-sm"
              />
              <select
                value={teacher.role}
                onChange={(e) => updateTeacher(index, "role", e.target.value)}
                className="border border-gray-300 rounded px-2 py-1 text-sm"
              >
                <option value="teacher">Profesor</option>
                <option value="admin">Administrador</option>
                <option value="viewer">Observador</option>
              </select>
              <input
                type="text"
                placeholder="Teléfono (opcional)"
                value={teacher.phone}
                onChange={(e) => updateTeacher(index, "phone", e.target.value)}
                className="border border-gray-300 rounded px-2 py-1 text-sm"
              />
            </div>

            <div className="mt-3">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Cursos Asignados
              </label>
              <div className="max-h-32 overflow-y-auto border border-gray-200 rounded p-2 space-y-1">
                {formData.courses.length > 0 ? (
                  formData.courses.map((course, courseIndex) => (
                    <label
                      key={courseIndex}
                      className="flex items-center text-xs"
                    >
                      <input
                        type="checkbox"
                        checked={teacher.courses.includes(course.name)}
                        onChange={(e) => {
                          const newCourses = e.target.checked
                            ? [...teacher.courses, course.name]
                            : teacher.courses.filter((c) => c !== course.name);
                          updateTeacher(index, "courses", newCourses);
                        }}
                        className="mr-2 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <span>{course.name}</span>
                    </label>
                  ))
                ) : (
                  <div className="text-xs text-gray-500 italic">
                    No hay cursos definidos. Primero selecciona los niveles
                    educativos en el paso anterior.
                  </div>
                )}
              </div>
            </div>

            <div className="mt-3">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Dimensiones Permitidas
              </label>
              <div className="max-h-32 overflow-y-auto border border-gray-200 rounded p-2 space-y-1">
                <label className="flex items-center text-xs mb-1">
                  <input
                    type="checkbox"
                    checked={teacher.allowedDimensions.length === 0}
                    onChange={(e) => {
                      if (e.target.checked) {
                        updateTeacher(index, "allowedDimensions", []);
                      }
                    }}
                    className="mr-2 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="font-medium">Todas las dimensiones</span>
                </label>
                {dimensionsTemplate.map((dimension) => (
                  <label
                    key={dimension.id}
                    className="flex items-center text-xs"
                  >
                    <input
                      type="checkbox"
                      checked={teacher.allowedDimensions.includes(dimension.id)}
                      onChange={(e) => {
                        const newDimensions = e.target.checked
                          ? [...teacher.allowedDimensions, dimension.id]
                          : teacher.allowedDimensions.filter(
                              (d) => d !== dimension.id
                            );
                        updateTeacher(
                          index,
                          "allowedDimensions",
                          newDimensions
                        );
                      }}
                      className="mr-2 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span>{dimension.name}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>

      {formData.teachers.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          <p>No hay profesores agregados. Haz clic en "Agregar Profesor"</p>
        </div>
      )}
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h4 className="text-lg font-medium text-gray-900">
          Estudiantes ({formData.students.length})
        </h4>
        <div className="flex space-x-2">
          <button
            onClick={addStudent}
            className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-sm"
          >
            Agregar Estudiante
          </button>
          <button
            onClick={() => {
              console.log("🖱️ CLICK EN BOTÓN IMPORTAR ESTUDIANTES");
              studentFileInputRef.current?.click();
            }}
            className="bg-green-500 hover:bg-green-600 text-white px-3 py-1 rounded text-sm"
          >
            <FaUpload className="inline mr-1" />
            Importar CSV
          </button>
          <input
            ref={studentFileInputRef}
            type="file"
            accept=".csv"
            className="hidden"
            onChange={(e) => {
              try {
                const file = e.target.files?.[0];
                if (file) {
                  console.log("ℹ️ Importando estudiantes desde CSV", `${file?.name} (${file?.size} bytes)`);
                  importStudentsFromCSV(file);
                  // Reset input para permitir seleccionar el mismo archivo de nuevo
                  e.target.value = "";
                } else {
                  console.log("❌ No se detectó archivo");
                }
              } catch (error) {
                console.error("❌ Error en onChange estudiantes:", error);
              }
            }}
          />
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h5 className="font-medium text-blue-900 mb-2">
          Formato CSV Soportado:
        </h5>
        <div className="text-sm text-blue-800">
          <p className="mb-1">
            <strong>Formato específico:</strong> Curso, Alumno, RUT, Fecha de
            Nacimiento, Sexo
          </p>
          <p className="mb-1">
            <strong>Ejemplo:</strong> Kinder A, Álamos Rubio Domingo, 268457771,
            24-05-19, M
          </p>
          <p>
            <strong>Formato genérico:</strong> Con headers como id, name,
            course, gender, birthDate, email
          </p>
        </div>
      </div>

      <div className="space-y-4 max-h-96 overflow-y-auto">
        {formData.students.map((student, index) => (
          <div key={index} className="border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h5 className="font-medium text-gray-900">
                Estudiante {index + 1}
              </h5>
              <button
                onClick={() => removeStudent(index)}
                className="text-red-500 hover:text-red-700"
              >
                <FaTimes />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <input
                type="text"
                placeholder="RUT/ID"
                value={student.id}
                onChange={(e) => updateStudent(index, "id", e.target.value)}
                className="border border-gray-300 rounded px-2 py-1 text-sm"
              />
              <input
                type="text"
                placeholder="Nombre completo"
                value={student.name}
                onChange={(e) => updateStudent(index, "name", e.target.value)}
                className="border border-gray-300 rounded px-2 py-1 text-sm"
              />
              <input
                type="text"
                placeholder="Nombre"
                value={student.firstName}
                onChange={(e) =>
                  updateStudent(index, "firstName", e.target.value)
                }
                className="border border-gray-300 rounded px-2 py-1 text-sm"
              />
              <input
                type="text"
                placeholder="Apellido"
                value={student.lastName}
                onChange={(e) =>
                  updateStudent(index, "lastName", e.target.value)
                }
                className="border border-gray-300 rounded px-2 py-1 text-sm"
              />
              <select
                value={student.course}
                onChange={(e) => updateStudent(index, "course", e.target.value)}
                className="border border-gray-300 rounded px-2 py-1 text-sm"
              >
                <option value="">Seleccionar curso</option>
                {formData.courses.map((course, courseIndex) => (
                  <option key={courseIndex} value={course.name}>
                    {course.name}
                  </option>
                ))}
              </select>
              <select
                value={student.gender}
                onChange={(e) => updateStudent(index, "gender", e.target.value)}
                className="border border-gray-300 rounded px-2 py-1 text-sm"
              >
                <option value="M">Masculino</option>
                <option value="F">Femenino</option>
              </select>
            </div>

            <div className="grid grid-cols-2 gap-2 mt-2">
              <input
                type="date"
                placeholder="Fecha de nacimiento"
                value={student.birthDate}
                onChange={(e) =>
                  updateStudent(index, "birthDate", e.target.value)
                }
                className="border border-gray-300 rounded px-2 py-1 text-sm"
              />
              <input
                type="email"
                placeholder="Email (opcional)"
                value={student.email}
                onChange={(e) => updateStudent(index, "email", e.target.value)}
                className="border border-gray-300 rounded px-2 py-1 text-sm"
              />
            </div>
          </div>
        ))}
      </div>

      {formData.students.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          <p>No hay estudiantes agregados. Haz clic en "Agregar Estudiante"</p>
        </div>
      )}
    </div>
  );

  const renderStep4 = () => (
    <div className="space-y-8">
      {/* Información de Cursos Generados */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="text-lg font-semibold text-blue-900 mb-2">
          Cursos y Asignación
        </h3>
        <p className="text-sm text-blue-800">
          Se han generado {formData.courses.length} cursos basados en los
          niveles seleccionados. Puedes asignar profesores a cada curso.
        </p>
      </div>

      {/* Lista de Cursos Generados */}
      {formData.courses.length > 0 && (
        <div className="bg-gray-50 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              Cursos Generados ({formData.courses.length})
            </h3>
            <button
              onClick={addCourse}
              className="bg-green-500 hover:bg-green-600 text-white px-3 py-1 rounded text-sm"
            >
              Agregar Curso Manual
            </button>
          </div>

          <div className="space-y-4 max-h-96 overflow-y-auto">
            {formData.courses.map((course, index) => (
              <div
                key={index}
                className="border border-gray-200 rounded-lg p-4 bg-white"
              >
                <div className="flex items-center justify-between mb-3">
                  <h5 className="font-medium text-gray-900">{course.name}</h5>
                  <button
                    onClick={() => removeCourse(index)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <FaTimes />
                  </button>
                </div>

                <div className="grid grid-cols-2 gap-3 mb-3">
                  <input
                    type="text"
                    placeholder="Nombre del curso"
                    value={course.name}
                    onChange={(e) =>
                      updateCourse(index, "name", e.target.value)
                    }
                    className="border border-gray-300 rounded px-2 py-1 text-sm"
                  />
                  <input
                    type="text"
                    placeholder="Nivel"
                    value={course.level}
                    onChange={(e) =>
                      updateCourse(index, "level", e.target.value)
                    }
                    className="border border-gray-300 rounded px-2 py-1 text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Profesores Asignados
                  </label>
                  {formData.teachers.length > 0 ? (
                    <div className="space-y-2 max-h-32 overflow-y-auto border border-gray-200 rounded p-2">
                      {formData.teachers.map((teacher, teacherIndex) => (
                        <label
                          key={teacherIndex}
                          className="flex items-center text-sm"
                        >
                          <input
                            type="checkbox"
                            checked={course.teacherEmails.includes(
                              teacher.email
                            )}
                            onChange={(e) => {
                              const newEmails = e.target.checked
                                ? [...course.teacherEmails, teacher.email]
                                : course.teacherEmails.filter(
                                    (email) => email !== teacher.email
                                  );
                              updateCourse(index, "teacherEmails", newEmails);
                            }}
                            className="mr-2 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <span>
                            {teacher.firstName} {teacher.lastName} (
                            {teacher.email})
                          </span>
                        </label>
                      ))}
                    </div>
                  ) : (
                    <div className="text-sm text-gray-500 italic border border-gray-200 rounded p-2">
                      No hay profesores definidos. Agrégalos en el paso
                      anterior.
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {formData.courses.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          <p>
            Selecciona los niveles y genera los cursos automáticamente, o agrega
            cursos manualmente.
          </p>
        </div>
      )}
    </div>
  );

  const renderStep5 = () => (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h3 className="text-xl font-semibold text-gray-900 mb-2">
          Revisión Final
        </h3>
        <p className="text-gray-600">
          Revisa todos los datos antes de crear la organización
        </p>
      </div>

      <div className="space-y-6">
        {/* Información Básica */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="font-semibold text-gray-900 mb-3">
            Información Básica
          </h4>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-600">Nombre:</span>
              <span className="ml-2 font-medium">{formData.displayName}</span>
            </div>
            <div>
              <span className="text-gray-600">Tenant Key:</span>
              <span className="ml-2 font-medium">{formData.tenantKey}</span>
            </div>
            <div>
              <span className="text-gray-600">Dominios:</span>
              <span className="ml-2 font-medium">
                {formData.domains.join(", ")}
              </span>
            </div>
            <div>
              <span className="text-gray-600">Estado:</span>
              <span className="ml-2 font-medium">{formData.status}</span>
            </div>
          </div>
        </div>

        {/* Administrador */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="font-semibold text-gray-900 mb-3">Administrador</h4>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-600">Usuario:</span>
              <span className="ml-2 font-medium">
                {formData.adminUser.username}
              </span>
            </div>
            <div>
              <span className="text-gray-600">Email:</span>
              <span className="ml-2 font-medium">
                {formData.adminUser.email}
              </span>
            </div>
            <div>
              <span className="text-gray-600">Nombre:</span>
              <span className="ml-2 font-medium">
                {formData.adminUser.firstName} {formData.adminUser.lastName}
              </span>
            </div>
          </div>
        </div>

        {/* Branding */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="font-semibold text-gray-900 mb-3">Branding</h4>
          <div className="flex items-center space-x-4">
            {logoPreview && (
              <img
                src={logoPreview}
                alt="Logo"
                className="w-12 h-12 object-contain"
              />
            )}
            <div className="flex space-x-4">
              <div className="flex items-center">
                <div
                  className="w-6 h-6 rounded border mr-2"
                  style={{ backgroundColor: formData.branding.colors.primary }}
                ></div>
                <span className="text-sm text-gray-600">Primario</span>
              </div>
              <div className="flex items-center">
                <div
                  className="w-6 h-6 rounded border mr-2"
                  style={{
                    backgroundColor: formData.branding.colors.secondary,
                  }}
                ></div>
                <span className="text-sm text-gray-600">Secundario</span>
              </div>
            </div>
          </div>
        </div>

        {/* Estadísticas */}
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-blue-50 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-blue-600">
              {formData.teachers.length}
            </div>
            <div className="text-sm text-blue-800">Profesores</div>
          </div>
          <div className="bg-green-50 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-green-600">
              {formData.students.length}
            </div>
            <div className="text-sm text-green-800">Estudiantes</div>
          </div>
          <div className="bg-purple-50 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-purple-600">
              {formData.courses.length}
            </div>
            <div className="text-sm text-purple-800">Cursos</div>
          </div>
        </div>

        {/* Advertencias */}
        {(formData.teachers.length === 0 || formData.courses.length === 0) && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-center">
              <div className="text-yellow-600 mr-2">⚠️</div>
              <div>
                <h5 className="font-medium text-yellow-800">Advertencias</h5>
                <ul className="text-sm text-yellow-700 mt-1">
                  {formData.teachers.length === 0 && (
                    <li>• No hay profesores definidos</li>
                  )}
                  {formData.courses.length === 0 && (
                    <li>• No hay cursos definidos</li>
                  )}
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const getCurrentStepContent = () => {
    switch (currentStep) {
      case 1:
        return renderStep1();
      case 2:
        return renderStep2();
      case 3:
        return renderStep3();
      case 4:
        return renderStep4();
      case 5:
        return renderStep5();
      default:
        return renderStep1();
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[95vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">
              Crear Nueva Organización
            </h2>
            <button
              onClick={onCancel}
              className="text-gray-400 hover:text-gray-600"
            >
              <FaTimes size={20} />
            </button>
          </div>

          {/* Progress Steps */}
          <div className="flex items-center justify-between mt-4">
            {steps.map((step, index) => (
              <div key={step.id} className="flex items-center">
                <div
                  className={`flex items-center justify-center w-8 h-8 rounded-full ${
                    currentStep >= step.id
                      ? "bg-blue-500 text-white"
                      : "bg-gray-200 text-gray-600"
                  }`}
                >
                  {currentStep > step.id ? (
                    <FaCheck size={14} />
                  ) : (
                    <step.icon size={14} />
                  )}
                </div>
                <span
                  className={`ml-2 text-sm ${
                    currentStep >= step.id
                      ? "text-blue-600 font-medium"
                      : "text-gray-500"
                  }`}
                >
                  {step.title}
                </span>
                {index < steps.length - 1 && (
                  <div
                    className={`w-8 h-0.5 mx-4 ${
                      currentStep > step.id ? "bg-blue-500" : "bg-gray-200"
                    }`}
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
              {error}
            </div>
          )}

          {/* Sistema de Progreso Visual */}
          {creationProgress.isActive && (
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-6 mb-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  🚀 Creando tu Organización
                </h3>
                <div className="text-sm text-gray-600">
                  {creationProgress.overallProgress}% Completado
                </div>
              </div>

              {/* Barra de progreso general */}
              <div className="w-full bg-gray-200 rounded-full h-3 mb-4 overflow-hidden">
                <div 
                  className="bg-gradient-to-r from-blue-500 to-indigo-600 h-3 rounded-full transition-all duration-500 ease-out"
                  style={{ width: `${creationProgress.overallProgress}%` }}
                >
                  <div className="h-full bg-white opacity-20 animate-pulse"></div>
                </div>
              </div>

              {/* Tiempo estimado */}
              <div className="text-center text-sm text-gray-600 mb-6">
                ⏱️ Tiempo restante estimado: <strong>{creationProgress.estimatedTimeRemaining}</strong>
              </div>

              {/* Pasos individuales */}
              <div className="space-y-4">
                {creationProgress.steps.map((step, index) => (
                  <div key={step.id} className="flex items-center space-x-4">
                    {/* Icono del paso */}
                    <div className={`flex items-center justify-center w-10 h-10 rounded-full text-lg ${
                      step.status === 'completed' ? 'bg-green-100 text-green-600' :
                      step.status === 'in-progress' ? 'bg-blue-100 text-blue-600 animate-pulse' :
                      step.status === 'error' ? 'bg-red-100 text-red-600' :
                      'bg-gray-100 text-gray-400'
                    }`}>
                      {step.status === 'completed' ? '✅' : 
                       step.status === 'error' ? '❌' : step.icon}
                    </div>

                    {/* Información del paso */}
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <span className={`font-medium ${
                          step.status === 'completed' ? 'text-green-700' :
                          step.status === 'in-progress' ? 'text-blue-700' :
                          step.status === 'error' ? 'text-red-700' :
                          'text-gray-500'
                        }`}>
                          {step.name}
                        </span>
                        <span className="text-sm text-gray-600">
                          {step.current}/{step.total}
                        </span>
                      </div>

                      {/* Barra de progreso del paso */}
                      <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                        <div 
                          className={`h-2 rounded-full transition-all duration-300 ${
                            step.status === 'completed' ? 'bg-green-500' :
                            step.status === 'in-progress' ? 'bg-blue-500' :
                            step.status === 'error' ? 'bg-red-500' :
                            'bg-gray-300'
                          }`}
                          style={{ 
                            width: `${step.total > 0 ? (step.current / step.total) * 100 : 0}%` 
                          }}
                        />
                      </div>
                    </div>

                    {/* Estado actual */}
                    <div className="text-xs text-gray-500 min-w-[80px] text-right">
                      {step.status === 'completed' && '✅ Listo'}
                      {step.status === 'in-progress' && '⚡ Procesando...'}
                      {step.status === 'error' && '❌ Error'}
                      {step.status === 'pending' && '⏳ Esperando...'}
                    </div>
                  </div>
                ))}
              </div>

              {/* Paso actual destacado */}
              {creationProgress.currentStep && (
                <div className="mt-4 p-3 bg-white rounded-lg border border-blue-200">
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                    <span className="text-sm font-medium text-blue-800">
                      {creationProgress.steps.find(s => s.id === creationProgress.currentStep)?.name || 'Procesando...'}
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}

          {getCurrentStepContent()}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex items-center justify-between">
          <button
            onClick={() => setCurrentStep(Math.max(1, currentStep - 1))}
            disabled={currentStep === 1}
            className="flex items-center px-4 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FaArrowLeft className="mr-2" />
            Anterior
          </button>

          <div className="text-sm text-gray-500">
            Paso {currentStep} de {steps.length}
          </div>

          {currentStep < steps.length ? (
            <button
              onClick={async () => {
                // Validaciones por paso
                if (currentStep === 1) {
                  // Validar información básica
                  if (
                    !formData.displayName ||
                    !formData.tenantKey ||
                    !formData.domains[0] ||
                    !formData.adminUser.username ||
                    !formData.adminUser.email ||
                    !formData.adminUser.firstName ||
                    !formData.adminUser.lastName ||
                    !formData.adminUser.password
                  ) {
                    setError(
                      "Por favor completa todos los campos obligatorios"
                    );
                    return;
                  }

                  if (selectedLevels.length === 0) {
                    setError(
                      "Por favor selecciona al menos un nivel educativo"
                    );
                    return;
                  }

                  // Validar logo (opcional pero recomendado)
                  if (!logoFile && !formData.branding.logoUrl) {
                    const confirmWithoutLogo = window.confirm(
                      "No has seleccionado un logo para la organización. ¿Deseas continuar sin logo?"
                    );
                    if (!confirmWithoutLogo) {
                      return;
                    }
                  }

                  try {
                    // Crear la organización después del paso 1
                    await createOrganizationEarly();
                    // Generar cursos automáticamente
                    generateCourses();
                  } catch (error) {
                    // El error ya se maneja en createOrganizationEarly
                    return;
                  }
                }

                if (currentStep === 2) {
                  // Validar que hay al menos un profesor
                  if (formData.teachers.length === 0) {
                    const confirmWithoutTeachers = window.confirm(
                      "No has agregado ningún profesor. ¿Deseas continuar sin profesores? Podrás agregarlos después."
                    );
                    if (!confirmWithoutTeachers) {
                      return;
                    }
                  }

                  // Validar que los profesores tienen datos completos
                  const incompleteTeachers = formData.teachers.filter(
                    (teacher) =>
                      !teacher.firstName ||
                      !teacher.lastName ||
                      !teacher.email ||
                      !teacher.username
                  );

                  if (incompleteTeachers.length > 0) {
                    setError(
                      `Hay ${incompleteTeachers.length} profesor(es) con información incompleta. Por favor completa todos los campos obligatorios.`
                    );
                    return;
                  }
                }

                if (currentStep === 3) {
                  // Validar estudiantes (opcional)
                  if (formData.students.length === 0) {
                    const confirmWithoutStudents = window.confirm(
                      "No has agregado ningún estudiante. ¿Deseas continuar sin estudiantes? Podrás agregarlos después."
                    );
                    if (!confirmWithoutStudents) {
                      return;
                    }
                  }

                  // Validar que los estudiantes tienen datos completos
                  const incompleteStudents = formData.students.filter(
                    (student) => !student.name || !student.course
                  );

                  if (incompleteStudents.length > 0) {
                    setError(
                      `Hay ${incompleteStudents.length} estudiante(s) con información incompleta. Por favor completa al menos el nombre y curso.`
                    );
                    return;
                  }
                }

                if (currentStep === 4) {
                  // Validar asignaciones de profesores a cursos (opcional)
                  const coursesWithoutTeachers = formData.courses.filter(
                    (course) => course.teacherEmails.length === 0
                  );

                  if (
                    coursesWithoutTeachers.length > 0 &&
                    formData.teachers.length > 0
                  ) {
                    const confirmWithoutAssignments = window.confirm(
                      `Hay ${coursesWithoutTeachers.length} curso(s) sin profesores asignados. ¿Deseas continuar? Podrás hacer las asignaciones después.`
                    );
                    if (!confirmWithoutAssignments) {
                      return;
                    }
                  }
                }

                setError(null);
                setCurrentStep(currentStep + 1);
              }}
              disabled={loading}
              className="flex items-center px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
            >
              {loading && currentStep === 1 ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Creando organización...
                </>
              ) : (
                <>
                  Siguiente
                  <FaArrowRight className="ml-2" />
                </>
              )}
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="flex items-center px-6 py-2 bg-green-500 text-white rounded hover:bg-green-600 disabled:opacity-50"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Creando...
                </>
              ) : (
                <>
                  <FaCheck className="mr-2" />
                  Crear Organización
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
